import MainLayout from "../../Layouts/MainLayout";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaCheck, FaPlus, FaCog } from "react-icons/fa";
import useAuth from "../../lib/Hooks/useAuth";
import { GetAllPackages, CreatePackage, MarkPackageHandled, UpdatePackage, CreateProductType, DeleteProductType, GetDeletedPackages, RestorePackage } from "../../lib/ApiHandler/PackageApi";
import { GetTorkSettings, UpsertTorkSettings } from "../../lib/ApiHandler/TorkSettingsApi";
import { setPackages, addPackage, removePackage, updatePackage, removeProductType } from "../../state/slices/PackageSlice";
import { setDeletedPackages, removeDeletedPackage } from "../../state/slices/DeletedPackageSlice";
import { setTorkSettings } from "../../state/slices/TorkSettingsSlice";
import { GetTimeSinceAction, GetHoursSinceAction } from "../../lib/ShapeHandler";
import { NotifyError } from "../../lib/Notify/notify";

const Tork = ({ jwtToken: jwtTokenProp }) => {
    const { jwtToken: jwtTokenHook } = useAuth();
    const jwtToken = jwtTokenProp || jwtTokenHook;
    const dispatch = useDispatch();

    const packages = useSelector(state => state.packages);
    const torkSettings = useSelector(state => state.torkSettings);
    const deletedPackages = useSelector(state => state.deletedPackages);

    const [activeTab, setActiveTab] = useState("active");

    const [showForm, setShowForm] = useState(false);
    const [quickFot, setQuickFot] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [editFot, setEditFot] = useState("");
    const [editNote, setEditNote] = useState("");
    const [editPlacedAt, setEditPlacedAt] = useState("");
    const [newProductName, setNewProductName] = useState("");
    const [blockMsg, setBlockMsg] = useState(null); // { name, msg }
    const [settingsEdit, setSettingsEdit] = useState({ maxtidHours: "", yellowAtHours: "", redAtHours: "" });
    const [confirmHandledPkg, setConfirmHandledPkg] = useState(null);
    const [confirmDeleteType, setConfirmDeleteType] = useState(null); // product type name

    useEffect(() => {
        if (!jwtToken) return;
        GetAllPackages(jwtToken, (result) => {
            dispatch(setPackages(result));
        });
        GetTorkSettings(jwtToken, (result) => {
            if (result) {
                dispatch(setTorkSettings(result));
                setSettingsEdit({ maxtidHours: result.maxtidHours, yellowAtHours: result.yellowAtHours, redAtHours: result.redAtHours });
            }
        });
    }, [dispatch, jwtToken]);

    /* Group packages by package name (columns), oldest first within each.
       Placeholder entries anchor the column but are not shown as rows. */
    const groupedPackages = useMemo(() => {
        const groups = {};
        packages.forEach(pkg => {
            const key = pkg.name || "Okänt paket";
            if (!groups[key]) groups[key] = [];
            if (!pkg.isPlaceholder) groups[key].push(pkg);
            else if (!groups[key]) groups[key] = []; // ensure column exists
        });
        // Also ensure placeholder-only columns appear
        packages.filter(p => p.isPlaceholder).forEach(p => {
            if (!groups[p.name]) groups[p.name] = [];
        });
        Object.values(groups).forEach(list =>
            list.sort((a, b) => new Date(a.placedAt) - new Date(b.placedAt))
        );
        return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
    }, [packages]);

    /* Product type names that have a placeholder */
    const productTypes = useMemo(() =>
        [...new Set(packages.filter(p => p.isPlaceholder).map(p => p.name))].sort(),
    [packages]);

    const handleAddProduct = (e) => {
        e.preventDefault();
        if (!newProductName.trim()) return;
        CreateProductType(newProductName.trim(), jwtToken, (created) => {
            dispatch(addPackage(created));
            setNewProductName("");
        });
    };

    const handleDeleteProduct = (productName) => {
        setBlockMsg(null);
        DeleteProductType(
            productName,
            jwtToken,
            () => dispatch(removeProductType(productName)),
            (msg) => setBlockMsg({ name: productName, msg })
        );
    };

    const handleHandled = (pkg) => {
        MarkPackageHandled(pkg, jwtToken, (softDeleted) => {
            dispatch(removePackage(pkg));
            dispatch(setDeletedPackages([softDeleted, ...deletedPackages]));
            setConfirmHandledPkg(null);
        });
    };

    const handleQuickAdd = (pkgName, e) => {
        e.preventDefault();
        const fot = (quickFot[pkgName] || "").trim();
        if (!fot) { NotifyError("Ange en fot."); return; }
        CreatePackage({ name: pkgName, fotName: fot, note: "" }, jwtToken, (created) => {
            dispatch(addPackage(created));
            setQuickFot(prev => ({ ...prev, [pkgName]: "" }));
        });
    };

    const startEdit = (pkg) => {
        setEditingId(pkg.id);
        setEditFot(pkg.fotName || "");
        setEditNote(pkg.note || "");
        // Convert ISO date to datetime-local format (YYYY-MM-DDTHH:MM)
        const dt = pkg.placedAt ? new Date(pkg.placedAt) : new Date();
        const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
            .toISOString().slice(0, 16);
        setEditPlacedAt(local);
    };

    const cancelEdit = () => setEditingId(null);

    const saveEdit = (pkg) => {
        UpdatePackage({ ...pkg, fotName: editFot.trim(), note: editNote.trim(), placedAt: editPlacedAt ? new Date(editPlacedAt).toISOString() : pkg.placedAt }, jwtToken, (updated) => {
            dispatch(updatePackage(updated));
            setEditingId(null);
        });
    };

    const handleSaveSettings = () => {
        const payload = {
            maxtidHours: parseInt(settingsEdit.maxtidHours) || 0,
            yellowAtHours: parseInt(settingsEdit.yellowAtHours) || 0,
            redAtHours: parseInt(settingsEdit.redAtHours) || 0,
        };
        UpsertTorkSettings(payload, jwtToken, (result) => {
            dispatch(setTorkSettings(result));
        });
    };

    const getRowStyle = (pkg) => {
        if (!torkSettings || !pkg?.placedAt) return { bg: "", timeText: "text-gray-500" };
        const ageHours = (Date.now() - new Date(pkg.placedAt).getTime()) / 3600000;
        if (ageHours >= torkSettings.redAtHours) return { bg: "bg-red-200", timeText: "text-red-700" };
        if (ageHours >= torkSettings.yellowAtHours) return { bg: "bg-yellow-100", timeText: "text-amber-600" };
        return { bg: "", timeText: "text-gray-500" };
    };

    const handleShowHistory = () => {
        setActiveTab("history");
        if (!jwtToken) return;
        GetDeletedPackages(jwtToken, (result) => {
            dispatch(setDeletedPackages(result));
        });
    };

    const handleRestore = (pkg) => {
        RestorePackage(pkg, jwtToken, (restored, newPlaceholder) => {
            dispatch(removeDeletedPackage(restored));
            dispatch(addPackage(restored));
            if (newPlaceholder) dispatch(addPackage(newPlaceholder));
        });
    };

    /* ------------------------------------------------------------------ */

    return (
        <MainLayout>
            <div className="p-6 flex flex-col mx-auto w-full container">

                {/* Header bar */}
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-xl font-semibold text-gray-800">In i tork</h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowForm(v => !v)}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                        >
                            <FaCog size={10} /> Inställningar
                        </button>
                    </div>
                </div>

                {/* Tab bar */}
                <div className="flex gap-1 mb-4 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab("active")}
                        className={`px-4 py-1.5 text-xs font-medium rounded-t ${activeTab === "active" ? "bg-white border border-b-white border-gray-200 text-blue-600 -mb-px" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Aktiva
                    </button>
                    <button
                        onClick={handleShowHistory}
                        className={`px-4 py-1.5 text-xs font-medium rounded-t ${activeTab === "history" ? "bg-white border border-b-white border-gray-200 text-blue-600 -mb-px" : "text-gray-500 hover:text-gray-700"}`}
                    >
                        Historik
                    </button>
                </div>

                {/* Manage product types panel */}
                {activeTab === "active" && showForm && (
                    <div className="mb-5 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Hantera produkttyper</h2>

                        {/* Add new */}
                        <form onSubmit={handleAddProduct} className="flex items-center gap-2 mb-3">
                            <input
                                type="text"
                                value={newProductName}
                                onChange={e => setNewProductName(e.target.value)}
                                placeholder="Ny produkttyp, t.ex. VT 3"
                                className="text-sm border border-gray-300 rounded px-2 py-1 w-56 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                            />
                            <button type="submit" className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700">
                                <FaPlus size={10} /> Lägg till
                            </button>
                        </form>

                        {/* Existing product types */}
                        <div className="flex flex-wrap gap-2">
                            {productTypes.length === 0 && <p className="text-xs text-gray-400">Inga produkttyper skapade än.</p>}
                            {productTypes.map(pt => (
                                <div key={pt} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-full text-xs font-medium text-slate-700 shadow-sm">
                                    {pt}
                                    <button
                                        onClick={() => setConfirmDeleteType(pt)}
                                        className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
                                        title="Ta bort produkttyp"
                                    >✕</button>
                                </div>
                            ))}
                        </div>

                        {/* Tork timing settings */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Tidsinställningar</h3>
                            <div className="flex items-end gap-3 flex-wrap">
                                <label className="flex flex-col gap-0.5">
                                    <span className="text-xs text-gray-500">Maxtid (h)</span>
                                    <input
                                        type="number" min="1"
                                        value={settingsEdit.maxtidHours}
                                        onChange={e => setSettingsEdit(s => ({ ...s, maxtidHours: e.target.value }))}
                                        className="text-sm border border-gray-300 rounded px-2 py-1 w-24 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                                    />
                                </label>
                                <label className="flex flex-col gap-0.5">
                                    <span className="text-xs text-yellow-600">Gul varning (h)</span>
                                    <input
                                        type="number" min="1"
                                        value={settingsEdit.yellowAtHours}
                                        onChange={e => setSettingsEdit(s => ({ ...s, yellowAtHours: e.target.value }))}
                                        className="text-sm border border-yellow-400 rounded px-2 py-1 w-24 focus:ring-1 focus:ring-yellow-400 focus:outline-none"
                                    />
                                </label>
                                <label className="flex flex-col gap-0.5">
                                    <span className="text-xs text-red-600">Röd varning (h)</span>
                                    <input
                                        type="number" min="1"
                                        value={settingsEdit.redAtHours}
                                        onChange={e => setSettingsEdit(s => ({ ...s, redAtHours: e.target.value }))}
                                        className="text-sm border border-red-400 rounded px-2 py-1 w-24 focus:ring-1 focus:ring-red-400 focus:outline-none"
                                    />
                                </label>
                                <button
                                    onClick={handleSaveSettings}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                >
                                    Spara
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Card grid */}
                {activeTab === "active" && (groupedPackages.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-6">Inga produkttyper skapade. Klicka "Hantera produkttyper" för att lägga till.</p>
                ) : (
                    <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
                        {groupedPackages.map(([pkgName, list]) => (
                            <div key={pkgName} className="bg-white border border-slate-200 rounded-lg shadow-sm flex flex-col">

                                {/* Card header */}
                                <div className="bg-slate-700 text-white rounded-t-lg px-3 py-2 flex items-center justify-between">
                                    <span className="font-bold text-sm truncate">{pkgName}</span>
                                    <span className="ml-2 shrink-0 text-xs font-normal text-slate-300 bg-slate-600 rounded-full px-2 py-0.5">{list.length}</span>
                                </div>

                                {/* Sub-header */}
                                <div className="grid grid-cols-[1.5rem_1fr_auto] items-center gap-x-2 px-3 py-1 bg-slate-100 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                    <span className="text-center">#</span>
                                    <span>Utlägg</span>
                                    <span></span>
                                </div>

                                {/* Items */}
                                <div className="flex-1 divide-y divide-gray-100">
                                    {list.length === 0 && (
                                        <p className="text-xs text-gray-400 px-3 py-3 italic">Tom</p>
                                    )}
                                    {list.map((pkg, idx) => {
                                        const { bg, timeText } = getRowStyle(pkg);
                                        return (
                                            <div key={pkg.id} className={`px-3 py-2 ${bg}`}>
                                                {editingId === pkg.id ? (
                                                    <div className="flex flex-col gap-1" onClick={e => e.stopPropagation()}>
                                                        <input autoFocus value={editFot} onChange={e => setEditFot(e.target.value)} placeholder="Fot" className="text-xs border border-blue-400 rounded px-1 py-0.5 w-full outline-none" />
                                                        <input value={editNote} onChange={e => setEditNote(e.target.value)} placeholder="Anteckning" className="text-xs border border-gray-300 rounded px-1 py-0.5 w-full outline-none" />
                                                        <input type="datetime-local" value={editPlacedAt} onChange={e => setEditPlacedAt(e.target.value)} className="text-xs border border-gray-300 rounded px-1 py-0.5 w-full outline-none" />
                                                        <div className="flex gap-1">
                                                            <button onClick={() => saveEdit(pkg)} className="flex-1 text-xs text-white bg-blue-600 rounded py-0.5 hover:bg-blue-700">Spara</button>
                                                            <button onClick={cancelEdit} className="flex-1 text-xs text-gray-600 bg-gray-100 rounded py-0.5 hover:bg-gray-200">Avbryt</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-[1.5rem_1fr_auto] items-center gap-x-2 cursor-pointer group" onClick={() => startEdit(pkg)}>
                                                        <span className="text-center text-xs font-semibold text-gray-400">{idx + 1}</span>
                                                        <div className="min-w-0">
                                                            <p className="font-semibold text-gray-900 text-xs leading-tight group-hover:text-blue-600 truncate">{pkg.fotName}</p>
                                                            {pkg.note && <p className="text-gray-400 text-xs truncate">{pkg.note}</p>}
                                                            <p className={`text-xs ${timeText}`}>{GetTimeSinceAction(pkg.placedAt)} ({GetHoursSinceAction(pkg.placedAt)})</p>
                                                        </div>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setConfirmHandledPkg(pkg); }}
                                                            title="Markera som hanterad"
                                                            className="shrink-0 p-1.5 text-white bg-green-600 hover:bg-green-700 rounded"
                                                        >
                                                            <FaCheck size={10} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Quick-add footer */}
                                <div className="border-t border-gray-200 px-3 py-2">
                                    <form onSubmit={(e) => handleQuickAdd(pkgName, e)} className="flex items-center gap-1 rounded border border-dashed border-gray-300 px-2 py-1">
                                        <input
                                            type="text"
                                            value={quickFot[pkgName] || ""}
                                            onChange={e => setQuickFot(prev => ({ ...prev, [pkgName]: e.target.value }))}
                                            placeholder="+ fot"
                                            className="flex-1 text-xs bg-transparent outline-none placeholder-gray-400 min-w-0"
                                        />
                                        <button type="submit" title="Lägg till" className="shrink-0 text-blue-600 hover:text-blue-800">
                                            <FaPlus size={9} />
                                        </button>
                                    </form>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}

                {/* History tab */}
                {activeTab === "history" && (
                    <div>
                        {deletedPackages.length === 0 ? (
                            <p className="text-sm text-gray-500 mt-6">Ingen historik de senaste 7 dagarna.</p>
                        ) : (
                            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
                                <table className="w-full text-xs table-fixed">
                                    <colgroup>
                                        <col className="w-1/5" />
                                        <col className="w-1/5" />
                                        <col className="w-2/5" />
                                        <col style={{ width: "7rem" }} />
                                        <col style={{ width: "6rem" }} />
                                    </colgroup>
                                    <thead className="bg-slate-100 border-b border-slate-200">
                                        <tr>
                                            <th className="px-4 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Fot</th>
                                            <th className="px-4 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Produkttyp</th>
                                            <th className="px-4 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Hanterad av</th>
                                            <th className="px-4 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Borttagen</th>
                                            <th className="px-4 py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {[...deletedPackages]
                                            .sort((a, b) => new Date(b.deletedAt) - new Date(a.deletedAt))
                                            .map(pkg => (
                                            <tr key={pkg.id} className="hover:bg-slate-50">
                                                <td className="px-4 py-2">
                                                    <p className="font-semibold text-gray-900 truncate">{pkg.fotName}</p>
                                                    {pkg.note && <p className="text-gray-400 truncate">{pkg.note}</p>}
                                                </td>
                                                <td className="px-4 py-2 text-gray-500 truncate">{pkg.name}</td>
                                                <td className="px-4 py-2 text-gray-500 truncate">{pkg.handledBy || "–"}</td>
                                                <td className="px-4 py-2 text-gray-400 whitespace-nowrap">{GetTimeSinceAction(pkg.deletedAt)}</td>
                                                <td className="px-4 py-2 text-right">
                                                    <button
                                                        onClick={() => handleRestore(pkg)}
                                                        title="Återställ"
                                                        className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                                                    >
                                                        Återställ
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Confirm handled modal */}
            {confirmHandledPkg && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setConfirmHandledPkg(null)}>
                    <div className="bg-white rounded-xl shadow-xl p-6 w-80 max-w-full mx-4" onClick={e => e.stopPropagation()}>
                        <h2 className="text-base font-semibold text-gray-800 mb-1">Markera som hanterad?</h2>
                        <p className="text-sm text-gray-500 mb-5">
                            <span className="font-medium text-gray-700">{confirmHandledPkg.fotName}</span>
                            {" "}({confirmHandledPkg.name}) kommer att flyttas till historiken.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setConfirmHandledPkg(null)}
                                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                            >
                                Avbryt
                            </button>
                            <button
                                onClick={() => handleHandled(confirmHandledPkg)}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg"
                            >
                                Ja, hanterad
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Confirm delete product type modal */}
            {confirmDeleteType && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setConfirmDeleteType(null)}>
                    <div className="bg-white rounded-xl shadow-xl p-6 w-80 max-w-full mx-4" onClick={e => e.stopPropagation()}>
                        <h2 className="text-base font-semibold text-gray-800 mb-1">Ta bort produkttyp?</h2>
                        <p className="text-sm text-gray-500 mb-5">
                            Vill du verkligen ta bort <span className="font-medium text-gray-700">{confirmDeleteType}</span>?
                        </p>
                        {blockMsg?.name === confirmDeleteType && (
                            <p className="text-xs text-red-500 mb-3">{blockMsg.msg}</p>
                        )}
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => { setConfirmDeleteType(null); setBlockMsg(null); }}
                                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
                            >
                                Avbryt
                            </button>
                            <button
                                onClick={() => { handleDeleteProduct(confirmDeleteType); setConfirmDeleteType(null); }}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                            >
                                Ta bort
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </MainLayout>
    );
};

export default Tork;

